import { useForm } from "antd/es/form/Form";
import { message, Modal } from "antd";
import dayjs from "dayjs";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
    useNavigate,
    useParams,
    useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Alert } from "components/elements";

import * as service from "../services/fundApi";
import {
    emptyRateRule,
    emptyVestingRule,
} from "../schemas/FundSchemas";

import {
    setCurrentFund,
    setIsLoading,
} from "../slices/fundSlice";
import { PAYROLL_PAGE } from "features/payrollSetting/schemas/payrollSchema";

const DEFAULT_VALUES = {
    statusFlag: "ACTIVE",
    contributionRateConditions: {
        companyMatchNotExceedEmployeeRate: false,
        definedByPosition: false,
        scope: "ORGANIZATION",
        rules: [
            emptyRateRule(),
        ],
    },

    vestingConditions: {
        definedByPosition: false,
        scope: "ORGANIZATION",
        rules: [
            emptyVestingRule(),
        ],
    },

    entryExitCondition: {
        policy: "LIMITED_REJOIN_WITH_WAITING_PERIOD",
        maxRejoinCount: undefined,
        waitingPeriod: undefined,
        waitingPeriodUnit: "MONTH",
    },
};

function useFund() {
    const [form] = useForm();

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id } = useParams();

    const isEdit = Boolean(id);
    const location = useLocation();
    const isView = location.pathname.includes("/configuration/payroll/fund/view/");
    const isLoading = useSelector(
        (state) => state.fund.isLoading
    );

    const currentFund = useSelector(
        (state) => state.fund.currentFund
    );

    const [isSaving, setIsSaving] = useState(false);
    const [positionOptions, setPositionOptions] = useState([]);

    // =========================================================
    // Position Options
    // =========================================================

    const fetchPositionOptions = useCallback(async () => {
        try {
            const response = await service.getPositionList();

            const list =
                response?.data?.content ||
                response?.data ||
                [];

            setPositionOptions(
                list.map((p) => ({
                    value: p.id ?? p.positionId,

                    label:
                        i18n.language === "en"
                            ? p.nameEn ?? p.name
                            : p.name,
                }))
            );
        } catch (error) {
            // Position lookup is non-blocking.
            setPositionOptions([]);
        }
    }, [i18n.language]);

    useEffect(() => {
        fetchPositionOptions();
    }, [fetchPositionOptions]);

    // =========================================================
    // Response -> Form
    // =========================================================

    const mapResponseToFormValues = useCallback(
        (fund) => {
            return {
                ...fund,

                fundStartDate: fund?.fundStartDate
                    ? dayjs(fund.fundStartDate)
                    : undefined,

                contributionRateConditions: {
                    companyMatchNotExceedEmployeeRate:
                        fund?.contributionRateConditions
                            ?.companyMatchNotExceedEmployeeRate ??
                        false,

                    definedByPosition:
                        fund?.contributionRateConditions
                            ?.definedByPosition ??
                        false,

                    scope:
                        fund?.contributionRateConditions
                            ?.scope ||
                        "ORGANIZATION",

                    rules:
                        fund?.contributionRateConditions
                            ?.rules?.length
                            ? fund.contributionRateConditions.rules
                            : [
                                emptyRateRule(),
                            ],
                },

                vestingConditions: {
                    definedByPosition:
                        fund?.vestingConditions
                            ?.definedByPosition ??
                        false,

                    scope:
                        fund?.vestingConditions
                            ?.scope ||
                        "ORGANIZATION",

                    rules:
                        fund?.vestingConditions
                            ?.rules?.length
                            ? fund.vestingConditions.rules
                            : [
                                emptyVestingRule(),
                            ],
                },

                entryExitCondition:
                    fund?.entryExitCondition
                        ? {
                            ...fund.entryExitCondition,
                        }
                        : {
                            ...DEFAULT_VALUES.entryExitCondition,
                        },
            };
        },
        []
    );

    // =========================================================
    // Form -> API
    // =========================================================

    const mapFormValuesToPayload = useCallback(
        (values) => {
            return {
                ...values,

                fundStartDate:
                    values?.fundStartDate
                        ? values.fundStartDate.format(
                            "YYYY-MM-DD"
                        )
                        : null,
            };
        },
        []
    );

    // =========================================================
    // Get Fund By ID
    // =========================================================

    const fetchFund = useCallback(async () => {
        if (!id) {
            return;
        }

        try {
            dispatch(setIsLoading(true));

            const response =
                await service.getFundById(id);

            const fund = response?.data || {};

            dispatch(
                setCurrentFund(fund)
            );

            form.setFieldsValue(
                mapResponseToFormValues(fund)
            );
        } catch (error) {
            dispatch(
                setCurrentFund({})
            );

            Alert({
                type: "error",
                resultObject: error,
            });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [
        id,
        dispatch,
        form,
        mapResponseToFormValues,
    ]);

    // =========================================================
    // Initial Form
    // =========================================================

    useEffect(() => {
        if (isEdit) {
            fetchFund();
            return;
        }

        // Create mode
        dispatch(setCurrentFund({}));

        form.resetFields();

        form.setFieldsValue({
            ...DEFAULT_VALUES,

            contributionRateConditions: {
                ...DEFAULT_VALUES.contributionRateConditions,

                rules: [
                    emptyRateRule(),
                ],
            },

            vestingConditions: {
                ...DEFAULT_VALUES.vestingConditions,

                rules: [
                    emptyVestingRule(),
                ],
            },

            entryExitCondition: {
                ...DEFAULT_VALUES.entryExitCondition,
            },
        });

        dispatch(setIsLoading(false));
    }, [
        isEdit,
        fetchFund,
        dispatch,
        form,
    ]);

    // =========================================================
    // Submit
    // =========================================================

    const onFinish = useCallback(
        async (values) => {
            const payload =
                mapFormValuesToPayload(values);

            try {
                setIsSaving(true);

                if (isEdit) {

                    try {
                        const response = await service.updateFund(
                            id,
                            payload
                        );

                        console.log("response", response);

                        message.success(
                            t("common.success") || "บันทึกสำเร็จ"
                        );

                        navigate("/configuration/payroll", { state: { selectedTab: PAYROLL_PAGE.FUND } });

                    } catch (error) {
                        console.log("error", error);

                        message.error(
                            error?.response?.data?.message ||
                            error?.message ||
                            t("common.error") ||
                            "เพิ่มกองทุนไม่สำเร็จ"
                        );
                    }
                } else {
                    try {
                        const response = await service.createFund(payload);

                        console.log("response", response);

                        message.success(
                            t("common.success") || "เพิ่มกองทุนสำเร็จ"
                        );

                        navigate("/configuration/payroll", { state: { selectedTab: PAYROLL_PAGE.FUND } });

                    } catch (error) {
                        console.log("error", error);

                        message.error(
                            error?.response?.data?.message ||
                            error?.message ||
                            t("common.error") ||
                            "เพิ่มกองทุนไม่สำเร็จ"
                        );
                    }
                }

                dispatch(
                    setCurrentFund({})
                );


            } catch (error) {
                Alert({
                    type: "error",
                    resultObject: error,
                });
            } finally {
                setIsSaving(false);
            }
        },
        [
            id,
            isEdit,
            mapFormValuesToPayload,
            dispatch,
            navigate,
            t,
        ]
    );

    // =========================================================
    // Cancel
    // =========================================================

    const onCancel = useCallback(() => {
        Modal.confirm({
            title: t(
                "dialog.confirmation.header"
            ),

            content:
                t(
                    "dialog.confirmation.leaveWithoutSave"
                ) ||
                "ยกเลิกการแก้ไข ข้อมูลที่กรอกจะไม่ถูกบันทึก",

            okText: t("common.confirm"),

            cancelText: t("common.cancel"),

            onOk: () => {
                dispatch(
                    setCurrentFund({})
                );

                navigate("/configuration/payroll", { state: { selectedTab: PAYROLL_PAGE.FUND } });
            },
        });
    }, [
        dispatch,
        navigate,
        t,
    ]);


    const pageTitle = useMemo(() => {
        if (isView) {
            return t("fund.label.viewFund") || "กองทุน";
        }
        return isEdit
            ? t("fund.label.editFund") || "แก้ไขกองทุน"
            : t("fund.label.addFund") || "เพิ่มกองทุน";
    }, [isEdit, isView, t]);

    return {
        form,

        i18n,

        id,

        isEdit,

        isLoading,

        isSaving,

        currentFund,

        pageTitle,

        positionOptions,

        onFinish,

        onCancel,
    };
}

export default useFund;